<template>
    <div class="tile-content tile-content-secondary">
        <div class="grid-content">
            <div class="grid-item grid-item-full">
                <p>Request a 'Know Your Customer' page to be displayed to the customer.</p>
            </div>

            <div class="grid-item grid-item-half grid-item-break-half">
                <div class="field">
                    <label class="field-label stack-s"><strong>{{ getSummaryText('excludePolicy') }}</strong></label>
                    <v-radio
                        v-for="field in getOptionsList('excludePolicy')"
                        class="stack-m"
                        :key="`${field.name}-${field.label}`"
                        :group="field.name"
                        :label="field.label"
                        :value="field.value"
                        :model="action[field.name]"
                        @update="val => changeActionValue('excludePolicy', val)"
                    />
                </div>
            </div>

            <div class="grid-item grid-item-half grid-item-break-half">
                <div class="field">
                    <label class="field-label stack-s"><strong>{{ getSummaryText('isMandatory') }}</strong></label>
                    <v-radio
                        v-for="field in getOptionsList('isMandatory')"
                        class="stack-m"
                        :key="`${field.name}-${field.label}`"
                        :group="field.name"
                        :label="field.label"
                        :value="field.value"
                        :model="action[field.name]"
                        @update="val => changeActionValue('isMandatory', val)"
                    />
                </div>
            </div>

            <div class="grid-item grid-item-half grid-item-break-half">
                <div class="field">
                    <label class="field-label stack-s"><strong>{{ getSummaryText('promptPolicy') }}</strong></label>
                    <v-radio
                        v-for="field in getOptionsList('promptPolicy')"
                        class="stack-m"
                        :key="`${field.name}-${field.label}`"
                        :group="field.name"
                        :label="field.label"
                        :value="field.value"
                        :model="action[field.name]"
                        @update="val => changeActionValue('promptPolicy', val)"
                    />
                </div>
            </div>

            <div class="grid-item grid-item-full">
                <div class="field">
                    <label class="field-label stack-s"><strong>{{ getSummaryText('rejectedBeforeTransactionProcessPolicy') }}</strong></label>
                    <v-radio
                        v-for="field in getOptionsList('rejectedBeforeTransactionProcessPolicy')"
                        class="stack-m"
                        :key="`${field.name}-${field.label}`"
                        :group="field.name"
                        :label="field.label"
                        :value="field.value"
                        :model="action[field.name]"
                        @update="val => changeActionValue('rejectedBeforeTransactionProcessPolicy', val)"
                    />
                </div>
            </div>

            <div class="grid-item grid-item-full">
                <div class="field">
                    <label class="field-label stack-s"><strong>{{ getSummaryText('rejectedAfterTransactionProcessPolicy') }}</strong></label>
                    <v-radio
                        v-for="field in getOptionsList('rejectedAfterTransactionProcessPolicy')"
                        class="stack-m"
                        :key="`${field.name}-${field.label}`"
                        :group="field.name"
                        :label="field.label"
                        :value="field.value"
                        :model="action[field.name]"
                        @update="val => changeActionValue('rejectedAfterTransactionProcessPolicy', val)"
                    />
                </div>
            </div>

            <div class="grid-item grid-item-full">
                <div class="field">
                    <label class="field-label stack-s"><strong>{{ getSummaryText('optionalPolicy') }}</strong></label>
                    <v-radio
                        v-for="field in getOptionsList('optionalPolicy')"
                        class="stack-m"
                        :key="`${field.name}-${field.label}`"
                        :group="field.name"
                        :label="field.label"
                        :value="field.value"
                        :model="action[field.name]"
                        @update="val => changeActionValue('optionalPolicy', val)"
                    />
                    <v-inline-loader v-if="gatewaysIsFetching"/>
                    <v-select v-if="showAlternateGateways"
                              :disabled="gatewaysIsFetching"
                              :options="gatewayListOptions"
                              v-model="action.alternateGatewayAccountIfOptional"/>
                </div>
            </div>

            <div class="grid-item grid-item-half grid-item-break-half">
                <div class="field">
                    <label class="field-label stack-s"><strong>If optional display this currency on the bypass link</strong></label>
                    <v-select v-model="action.bypassCurrencyToDisplay" :options="currencies"/>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import {required} from 'vuelidate/lib/validators';

    import {CURRENCIES_LIST} from '@/data/currencies';
    import RequestKYCActionModel from '@/common/models/rules/actions/request-kyc-action-model';

    import vInlineLoader from '@/components/v-inline-loader';
    import {vRadio, vSelect} from '@/components/form';
    import BaseAction from '../base-action';
    import RequestKYCSummaryFields, {fieldLabels, fieldSummaries} from './request-kyc-summary-fields';

    const showGatewaysList = (value) => {
        const {allowUseAlternateGateway} = RequestKYCActionModel.OptionalPolicies;
        return value === allowUseAlternateGateway;
    };

    export default {
        name: 'request-kyc',
        extends: BaseAction,
        components: {vRadio, vSelect, vInlineLoader},
        data() {
            return {
                gatewaysIsFetching: false,
            };
        },
        validations() {
            return {
                action: {
                    excludePolicy: {
                        required,
                    },
                    isMandatory: {
                        required,
                    },
                    promptPolicy: {
                        required,
                    },
                    rejectedBeforeTransactionProcessPolicy: {
                        required,
                    },
                    rejectedAfterTransactionProcessPolicy: {
                        required,
                    },
                    optionalPolicy: {
                        required,
                    },
                    alternateGatewayAccountIfOptional: {
                        ...this.showAlternateGateways ? {
                            required,
                        } : {},
                    },
                },
            };
        },
        computed: {
            currencies() {
                // not updatable (not reactive) property
                return [
                    {
                        value: '',
                        label: 'None',
                    },
                    ...CURRENCIES_LIST,
                ];
            },
            showAlternateGateways() {
                return showGatewaysList(this.action.optionalPolicy);
            },
            gatewayListOptions() {
                if (this.gatewaysIsFetching) {
                    return [];
                }
                const {gateways} = this.$store.getters['rulesEngine/gateways'];
                return gateways.map(({fields}) => ({value: fields.id, label: fields.id}));
            },
        },
        methods: {
            async fetchGatewayAccounts() {
                this.gatewaysIsFetching = true;
                await this.$store.dispatch('rulesEngine/fetchGatewayAccounts');
                this.gatewaysIsFetching = false;
            },
            getSummaryText(name) {
                return fieldSummaries[name];
            },
            getOptionsList(name) {
                switch (name) {
                    case 'isMandatory':
                        return [
                            {
                                name: 'isMandatory',
                                value: true,
                                label: 'Mandatory',
                            },
                            {
                                name: 'isMandatory',
                                value: false,
                                label: 'Optional',
                            },
                        ];
                    default: {
                        const {policies} = RequestKYCSummaryFields.find(item => item.name === name);
                        return Object.keys(fieldLabels[name])
                            .map(value => ({
                                name,
                                value: policies[value],
                                label: fieldLabels[name][value],
                            }));
                    }
                }
            },
            changeActionValue(name, value) {
                const clone = this.action.cloneSelf({[name]: value});

                if (name === 'optionalPolicy') {
                    if (showGatewaysList(value)) {
                        this.fetchGatewayAccounts();
                    } else {
                        clone.alternateGatewayAccountIfOptional = null;
                        this.gatewaysIsFetching = false;
                    }
                }
                this.$emit('input', clone);
            },
            getValidationAggregate() {
                const fields = [
                    'excludePolicy',
                    'isMandatory',
                    'promptPolicy',
                    'rejectedBeforeTransactionProcessPolicy',
                    'rejectedAfterTransactionProcessPolicy',
                    'optionalPolicy',
                ];

                return {
                    name: 'request-kyc',
                    rules: [
                        {
                            name: `The Request Kyc alternate gateway is required`,
                            delegate: () => {
                                this.$v.action.alternateGatewayAccountIfOptional.$touch();
                                return this.$v.action.alternateGatewayAccountIfOptional.$error;
                            },
                        },
                        ...fields.map(name => ({
                            name: `The request-kyc ${fieldSummaries[name]} is required`,
                            delegate: () => {
                                this.$v.action[name].$touch();
                                return this.$v.action[name].$error;
                            },
                        })),
                    ],
                };
            },
        },
        created() {
            (async () => {
                await this.fetchGatewayAccounts();
            })();
        },
    };
</script>
